import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Refresh } from 'iconsax-reactjs';

import { apiErrorMessage, fetchTable } from '../api/client';
import { formatDateTime } from '../i18n';
import { ErrorBlock, LoadingBlock } from '../components/StateBlock';
import { CrudRecord } from '../crud/types';
import { allResourceConfigs, blockedResources } from '../crud/resourceConfigs';

type Status = 'ok' | 'error' | 'blocked';

type HealthRow = {
  resource: string;
  label: string;
  endpoint: string;
  status: Status;
  count: number | null;
  loaded: number | null;
  writeEnabled: boolean;
  detail?: string;
};

// Register Records use the report list endpoint, not the resource table endpoint.
const resourceEndpointOverride: Record<string, string> = {
  'reports/registers': 'reports/registers'
};

function endpointFor(resource: string) {
  return resourceEndpointOverride[resource] ?? resource;
}

export function ResourceHealthPage() {
  const [rows, setRows] = useState<HealthRow[] | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  async function runHealthCheck() {
    setFatalError(null);
    setRows(null);

    // Static "blocked" entries first — these are known-bad and never probed.
    const blocked: HealthRow[] = blockedResources.map((entry) => ({
      resource: entry.resource,
      label: entry.title,
      endpoint: endpointFor(entry.resource),
      status: 'blocked',
      count: null,
      loaded: null,
      writeEnabled: false,
      detail: entry.reason
    }));

    const activeResources = allResourceConfigs().map((config) => ({
      resource: config.resource,
      label: config.title,
      endpoint: endpointFor(config.resource),
      orderBy: config.orderBy,
      writeEnabled: !config.readOnly
    }));

    const results = await Promise.allSettled(
      activeResources.map((entry) =>
        fetchTable<CrudRecord>(entry.resource, { page: 1, limit: 5, orderBy: entry.orderBy }).then((table) => ({
          resource: entry.resource,
          count: table.count ?? 0,
          loaded: table.data?.length ?? 0
        }))
      )
    );

    const active: HealthRow[] = activeResources.map((entry, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        return {
          resource: entry.resource,
          label: entry.label,
          endpoint: entry.endpoint,
          status: 'ok',
          count: result.value.count,
          loaded: result.value.loaded,
          writeEnabled: entry.writeEnabled
        };
      }
      return {
        resource: entry.resource,
        label: entry.label,
        endpoint: entry.endpoint,
        status: 'error',
        count: null,
        loaded: null,
        writeEnabled: entry.writeEnabled,
        detail: apiErrorMessage(result.reason, `Unable to reach ${entry.label}`)
      };
    });

    setRows([...active, ...blocked]);
    setCheckedAt(new Date());

    // Fatal only if everything failed — usually a session/network problem.
    if (active.every((row) => row.status === 'error')) {
      setFatalError('All resource endpoints failed. The backend session may have expired.');
    }
  }

  useEffect(() => {
    void runHealthCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(() => {
    if (!rows) return { ok: 0, error: 0, blocked: 0, total: 0 };
    return {
      ok: rows.filter((r) => r.status === 'ok').length,
      error: rows.filter((r) => r.status === 'error').length,
      blocked: rows.filter((r) => r.status === 'blocked').length,
      total: rows.length
    };
  }, [rows]);

  if (fatalError) return <ErrorBlock message={fatalError} />;
  if (!rows) return <LoadingBlock />;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Resource Health
          </Typography>
          <Typography color="text.secondary">
            Read-only probe of every configured Laravel admin endpoint. No write requests are issued.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh size={18} />} onClick={() => void runHealthCheck()}>
          Re-check
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              Healthy
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, color: 'success.main' }}>
              {summary.ok}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              Errors
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, color: 'error.main' }}>
              {summary.error}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              Blocked
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, color: 'warning.dark' }}>
              {summary.blocked}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" variant="body2">
              Total endpoints
            </Typography>
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
              {summary.total}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {summary.error > 0 ? (
        <Alert severity="warning">
          {summary.error} endpoint{summary.error > 1 ? 's' : ''} returned an error. See the table below for details.
        </Alert>
      ) : null}

      {checkedAt ? (
        <Typography variant="caption" color="text.secondary">
          Last checked {formatDateTime(checkedAt)}
        </Typography>
      ) : null}

      <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Resource</TableCell>
              <TableCell>Endpoint</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell align="right">Loaded</TableCell>
              <TableCell>Access</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.resource} hover>
                <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {row.endpoint}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={row.status} />
                </TableCell>
                <TableCell align="right">{row.count ?? '—'}</TableCell>
                <TableCell align="right">{row.loaded ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.writeEnabled ? 'success' : 'default'}
                    variant={row.writeEnabled ? 'filled' : 'outlined'}
                    label={row.writeEnabled ? 'CRUD' : 'Read-only'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  {row.detail ? (
                    <Typography variant="caption" color="text.secondary">
                      {row.detail}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function StatusChip({ status }: { status: Status }) {
  if (status === 'ok') return <Chip size="small" color="success" label="Healthy" />;
  if (status === 'blocked') return <Chip size="small" color="warning" label="Blocked" />;
  return <Chip size="small" color="error" label="Error" />;
}
